import { useEffect, useMemo, useState } from 'react';
import { Box, Link } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { logEvent } from '../../../utils/eventLog';
import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { ROSTER } from '../../Roster/tokens';
import { fetchRosterClassification, type RosterClassificationRow } from '../../Roster/rosterClassification';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, TABLE_SX } from '../components/GradeTable';
import { exitCriteriaLabel } from '../components/ProgramEvaluationHeader';
import { DrilldownNotFound, UnknownListKey, fetchStudentList, type StudentList, type StudentRow } from '../drilldownApi';
import { PE_ROUTE, describeListKey, studentHref, type ListContext, loggableListKey } from '../listKeys';
import type { PeReport, ScoreUnit } from '../types';
import { EM_DASH, fmtLevel, fmtScale } from '../utils/drilldownFormat';
import { DetailCard, DrilldownFrame, DrilldownPage, NotFoundPage, type LoadedReport } from './DrilldownFrame';

export const NO_LONGER_ON_ROSTER = 'No longer on the roster';

export type RosterName = { first: string; last: string };
export const AMBIGUOUS = 'ambiguous';
export type NameMatch = RosterName | typeof AMBIGUOUS;
export type NameIndex = Map<string, NameMatch>;

export type RosterNames = { kind: 'loading' } | { kind: 'ready'; names: NameIndex } | { kind: 'failed' };

export const ROSTER_UNAVAILABLE = 'Roster unavailable';

export const NAME_NOT_ON_ROSTER = 'Name not on the roster';

export const NAME_AMBIGUOUS = 'Name ambiguous on the roster';

export function indexRosterNames(rows: RosterClassificationRow[] | null): NameIndex {
  const current = (rows ?? []).filter((row) => row.enrollment !== 'departed');
  const claims = (keysOf: (row: RosterClassificationRow) => string[]) => {
    const byKey = new Map<string, Set<RosterClassificationRow>>();
    for (const row of current) {
      for (const key of keysOf(row)) {
        const rowsClaiming = byKey.get(key) ?? new Set<RosterClassificationRow>();
        rowsClaiming.add(row);
        byKey.set(key, rowsClaiming);
      }
    }
    return byKey;
  };
  const matchOf = (rowsClaiming: Set<RosterClassificationRow>): NameMatch => {
    if (rowsClaiming.size !== 1) return AMBIGUOUS;
    for (const row of rowsClaiming) return { first: row.firstName ?? '', last: row.lastName ?? '' };
    return AMBIGUOUS;
  };
  const index: NameIndex = new Map();
  for (const [key, rowsClaiming] of claims((row) => [row.studentKey])) index.set(key, matchOf(rowsClaiming));
  for (const [key, rowsClaiming] of claims((row) => row.altKeys ?? [])) {
    if (!index.has(key)) index.set(key, matchOf(rowsClaiming));
  }
  return index;
}

export const rosterNameOf = (match: NameMatch | null): RosterName | null => (match && match !== AMBIGUOUS ? match : null);

export function fullName(match: NameMatch | null): string | null {
  const name = rosterNameOf(match);
  const full = name ? `${name.first} ${name.last}`.trim() : '';
  return full === '' ? null : full;
}

export function rosterNamesOf(rows: RosterClassificationRow[] | null): RosterNames {
  return rows === null ? { kind: 'failed' } : { kind: 'ready', names: indexRosterNames(rows) };
}

export function missingNameLabel(roster: RosterNames): string {
  if (roster.kind === 'failed') return ROSTER_UNAVAILABLE;
  if (roster.kind === 'loading') return 'Loading name…';
  return NO_LONGER_ON_ROSTER;
}

export function lookupName(index: NameIndex, keys: string[]): NameMatch | null {
  let ambiguous = false;
  for (const key of keys) {
    const match = index.get(key);
    if (match === undefined) continue;
    if (match !== AMBIGUOUS) return match;
    ambiguous = true;
  }
  return ambiguous ? AMBIGUOUS : null;
}

export function listContextOf(report: PeReport): ListContext {
  const cohortLabels: Record<string, string> = {};
  for (const c of report.drilldown?.cohorts ?? []) cohortLabels[c.cohort] = c.label;
  const gradeLabels: Record<string, string> = {};
  for (const g of report.drilldown?.grades ?? []) gradeLabels[String(g.grade)] = g.grade_label;
  return { cohortLabels, gradeLabels };
}

type Chip = { key: string; label: string; bg: string; fg: string };

const COHORT_CHIPS: Record<string, Chip> = {
  ltel: { key: 'ltel', ...ROSTER.chip.ltel },
  newcomer: { key: 'newcomer', ...ROSTER.chip.newcomer },
  dual: { key: 'dual', ...ROSTER.chip.dually_identified },
};
const EXITED_CHIP: Chip = { ...ROSTER.chip.former, key: 'exited', label: 'Exited' };
const MONITORED_CHIP: Chip = { ...ROSTER.chip.monitoring, key: 'monitored', label: 'Monitored' };

export function chipsFor(row: StudentRow, listKey: string): Chip[] {
  const [family, second] = listKey.split(':');
  const skip = (key: string) => family === key || (family === 'cohort' && second === key);
  const chips: Chip[] = [];
  for (const cohort of ['ltel', 'newcomer', 'dual']) {
    const chip = COHORT_CHIPS[cohort];
    if (chip && row.cohorts.includes(cohort) && !skip(cohort)) chips.push(chip);
  }
  const exited = row.cohorts.includes('exited') || ['reclassified', 'inactive', 'exited', 'exit_undated'].includes(row.standing);
  const monitored = row.cohorts.includes('monitored') || row.standing === 'monitoring';
  if (exited && !skip('exited')) chips.push(EXITED_CHIP);
  if (monitored && !skip('monitored')) chips.push(MONITORED_CHIP);
  return chips;
}

export const ALTERNATE_SCALE = 'alternate_access';

export function scoreCell(level: number | null, scaleScore: number | null | undefined, scale: string | undefined, unit: ScoreUnit): string {
  if (scale === ALTERNATE_SCALE) return level != null ? `${fmtLevel(level)} Alt.` : EM_DASH;
  return unit === 'scale_score' ? fmtScale(scaleScore) : fmtLevel(level);
}

export function sortRows(rows: StudentRow[], names: NameIndex): StudentRow[] {
  const sortName = (row: StudentRow) => {
    const name = rosterNameOf(lookupName(names, [row.studentKey, ...row.altKeys]));
    return name && fullName(name) ? `${name.last} ${name.first}` : null;
  };
  const rank = (grade: number) => (grade === -1 ? Number.POSITIVE_INFINITY : grade);
  return [...rows].sort((a, b) => {
    if (rank(a.grade) !== rank(b.grade)) return rank(a.grade) - rank(b.grade);
    const an = sortName(a);
    const bn = sortName(b);
    if (an === null && bn === null) return a.studentKey.localeCompare(b.studentKey);
    if (an === null) return 1;
    if (bn === null) return -1;
    return an.localeCompare(bn);
  });
}

const EMPTY_NAMES: NameIndex = new Map();

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; list: StudentList; nextOffset: number | null; loadingMore: boolean; pageSize: number }
  | { kind: 'missing'; message: string }
  | { kind: 'error' };

function messageFor(err: unknown): string {
  if (err instanceof DrilldownNotFound && err.reason === 'no_membership') {
    return 'This report was generated before student lists were kept. Regenerate it to open the students behind each count.';
  }
  if (err instanceof DrilldownNotFound || err instanceof UnknownListKey) return 'That list isn’t in this report.';
  return '';
}

function ChipPill({ chip }: { chip: Chip }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: '2px',
        borderRadius: '9999px',
        bgcolor: chip.bg,
        color: chip.fg,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {chip.label}
    </Box>
  );
}

function ListBody({ loaded, listKey }: { loaded: LoadedReport; listKey: string }) {
  const { report, generatedOn, version, schoolYear, endYear, unit } = loaded;
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [roster, setRoster] = useState<RosterNames>({ kind: 'loading' });
  const names = roster.kind === 'ready' ? roster.names : EMPTY_NAMES;
  const shownKey = state.kind === 'ready' && state.list.key ? state.list.key : listKey;
  const info = useMemo(() => describeListKey(shownKey, listContextOf(report)), [shownKey, report]);

  useEffect(() => {
    let active = true;
    fetchStudentList(listKey, { schoolYear, version })
      .then((list) => {
        if (active) setState({ kind: 'ready', list, nextOffset: list.nextOffset ?? null, loadingMore: false, pageSize: list.students.length });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message = messageFor(err);
        setState(message ? { kind: 'missing', message } : { kind: 'error' });
      });
    return () => {
      active = false;
    };
  }, [listKey, schoolYear, version]);

  useEffect(() => {
    let active = true;
    fetchRosterClassification().then((rows) => {
      if (active) setRoster(rosterNamesOf(rows));
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (state.kind !== 'ready') return;
    void logEvent('program_evaluation.list_viewed.v1', { schoolYear, listKey: loggableListKey(shownKey) }).catch(() => undefined);
  }, [state.kind, schoolYear, shownKey]);

  const loadMore = () => {
    if (state.kind !== 'ready' || state.nextOffset === null || state.loadingMore) return;
    const { list, nextOffset, pageSize } = state;
    setState({ ...state, loadingMore: true });
    fetchStudentList(listKey, { schoolYear: list.schoolYear, version: list.version, offset: nextOffset })
      .then((page) => {
        setState({
          kind: 'ready',
          list: { ...list, students: [...list.students, ...page.students] },
          nextOffset: page.students.length === 0 ? null : (page.nextOffset ?? null),
          loadingMore: false,
          pageSize,
        });
      })
      .catch(() => setState({ ...state, loadingMore: false }));
  };

  const fromCrumb = info.fromTo === PE_ROUTE ? null : { label: info.from.split(' · ').slice(1).join(' · ') || info.from, to: info.fromTo };
  const crumbs = [...(fromCrumb ? [fromCrumb] : []), { label: info.title }];

  if (state.kind === 'missing') return <NotFoundPage crumbs={crumbs} loaded={loaded} message={state.message} />;
  if (state.kind === 'error') {
    return (
      <DrilldownPage crumbs={crumbs} title={info.title}>
        <Box role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          Program Evaluation is temporarily unavailable
        </Box>
      </DrilldownPage>
    );
  }
  if (state.kind === 'loading') {
    return (
      <DrilldownPage crumbs={crumbs} title={info.title}>
        <Box aria-label="Loading students" sx={{ fontSize: 14, color: COLORS.muted }}>
          Loading…
        </Box>
      </DrilldownPage>
    );
  }

  const rows = state.nextOffset === null ? sortRows(state.list.students, names) : state.list.students;
  const count = state.list.count;
  const remaining = count - rows.length;
  const criteria = exitCriteriaLabel(report);
  const subtitle = `Spring ${endYear ?? ''} ACCESS${criteria ? ` · ${criteria}` : ''}. These are the students as the report counted them on ${generatedOn}, not today’s roster.`;

  return (
    <DrilldownPage
      crumbs={crumbs}
      title={`${count.toLocaleString('en-US')} student${count === 1 ? '' : 's'} ${info.verb}`}
      subtitle={subtitle}
      opened={`${info.from} · ${info.title}`}
    >
      <DetailCard
        foot={
          remaining > 0
            ? `Showing ${rows.length} of ${count}, sorted by grade. Open a student to see them as the report counted them.`
            : 'Sorted by grade. Open a student to see them as the report counted them.'
        }
      >
        <Box sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={{ ...CELL_SX, width: 190 }}>Student</Box>
            <Box sx={{ ...CELL_SX, width: 64 }}>Grade</Box>
            <Box sx={{ ...CELL_SX, width: 210 }}>School</Box>
            <Box sx={{ ...CELL_SX, width: 96 }}>Composite</Box>
            <Box sx={{ ...CELL_SX, width: 90 }}>Speaking</Box>
            <Box sx={{ ...CELL_SX, width: 90 }}>Years in EL</Box>
            <Box sx={FIRST_CELL_SX}>Also in</Box>
          </Box>
          {rows.length === 0 && (
            <Box sx={{ ...ROW_SX, color: COLORS.muted }}>
              <Box sx={FIRST_CELL_SX}>Nobody in this list.</Box>
            </Box>
          )}
          {rows.map((row) => {
            const name = lookupName(names, [row.studentKey, ...row.altKeys]);
            return (
              <Box key={row.studentKey} data-testid="student-row" sx={ROW_SX}>
                <Box sx={{ ...CELL_SX, width: 190 }}>
                  <Link
                    component={RouterLink}
                    to={studentHref(row.ref, listKey)}
                    state={APP_LINK_STATE}
                    underline="hover"
                    sx={{ color: fullName(name) ? COLORS.link : COLORS.muted, fontWeight: 600 }}
                  >
                    {fullName(name) ?? (name === AMBIGUOUS ? NAME_AMBIGUOUS : name ? NAME_NOT_ON_ROSTER : missingNameLabel(roster))}
                  </Link>
                </Box>
                <Box sx={{ ...CELL_SX, width: 64 }}>{row.gradeLabel || String(row.grade)}</Box>
                <Box sx={{ ...CELL_SX, width: 210 }}>{row.school || 'No school on file'}</Box>
                <Box sx={{ ...CELL_SX, width: 96 }}>{scoreCell(row.composite, row.compositeScale, row.scale, unit)}</Box>
                <Box sx={{ ...CELL_SX, width: 90 }}>{scoreCell(row.speaking, row.speakingScale, row.scale, unit)}</Box>
                <Box sx={{ ...CELL_SX, width: 90 }}>{row.yearsInProgram >= 1 ? row.yearsInProgram : EM_DASH}</Box>
                <Box sx={{ ...FIRST_CELL_SX, flexDirection: 'row', flexWrap: 'wrap', gap: 0.5 }}>
                  {chipsFor(row, shownKey).map((chip) => (
                    <ChipPill key={chip.key} chip={chip} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
        {state.nextOffset !== null && remaining > 0 && (
          <Box
            component="button"
            type="button"
            onClick={loadMore}
            disabled={state.loadingMore}
            sx={{
              alignSelf: 'flex-start',
              px: 2,
              py: 1,
              borderRadius: '8px',
              border: `1px solid ${COLORS.divider}`,
              bgcolor: '#FFFFFF',
              color: COLORS.link,
              font: 'inherit',
              fontWeight: 600,
              cursor: state.loadingMore ? 'default' : 'pointer',
            }}
          >
            {state.loadingMore ? 'Loading…' : `Show the next ${Math.min(remaining, Math.max(state.pageSize, 1))} students`}
          </Box>
        )}
      </DetailCard>
    </DrilldownPage>
  );
}

export function StudentListPage() {
  const { key = '' } = useParams<{ key: string }>();
  const crumbs = [{ label: describeListKey(key).title }];
  return (
    <DrilldownFrame crumbs={crumbs}>{(loaded) => <ListBody key={key} loaded={loaded} listKey={key} />}</DrilldownFrame>
  );
}
